import { BaseModel, BaseModelProps, ReactRenderer } from '@mail/3promo';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import modalImage from 'img/quota-landing/family/spaceToEach/modalImg.png';
import React from 'react';
import { IS_MOBILE_BROWSER, IS_WEBVIEW } from 'reactApp/appHelpers/configHelpers';
import { getFamilyAlbumsFeature, getFamilyFoldersFeature } from 'reactApp/modules/family/family.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { isCloudOrMailOrApiOverQuota } from 'reactApp/modules/userQuota/overquota.helpers';
import { store as reduxStore } from 'reactApp/store';

export const FAMILY_SPACE_TO_EACH_MODAL_ID = 'family-space-to-each-modal';

class PopupModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/FamilySpaceToEachModal/FamilySpaceToEachModal'));

const SuspendedComponent = () => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent />
        </React.Suspense>
    );
};

export const familySpaceToEachModal: ModelCreator = ({ models, render, stores }) => {
    return new PopupModel(
        FAMILY_SPACE_TO_EACH_MODAL_ID,
        models.base,
        new BaseModelProps(FAMILY_SPACE_TO_EACH_MODAL_ID, {
            store: new LocalStorageStore(FAMILY_SPACE_TO_EACH_MODAL_ID, stores.localStorage),
            renderer: new ReactRenderer(FAMILY_SPACE_TO_EACH_MODAL_ID, SuspendedComponent, render.react),
            showLimit: 2,
            templateData: {},
            ignoreTestOnRedraw: true,
            preloadingImages: [modalImage],
            test: (resolve, reject) => {
                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);

                if (
                    storage !== EStorageType.home &&
                    storage !== EStorageType.familyFolders &&
                    storage !== EStorageType.familyAlbums &&
                    storage !== EStorageType.gallery &&
                    storage !== EStorageType.albums &&
                    storage !== EStorageType.documents &&
                    storage !== EStorageType.feed
                ) {
                    reject();
                    return;
                }

                const isBizUser = UserSelectors.isBizUser(state);
                const isCorpUser = UserSelectors.isCorpUser(state);
                const isOverquota = isCloudOrMailOrApiOverQuota(state);
                const hasFamilyFoldersFeature = getFamilyFoldersFeature(state);
                const hasFamilyAlbumsFeature = getFamilyAlbumsFeature(state);
                const isFamilyUser = hasFamilyFoldersFeature && hasFamilyAlbumsFeature;

                const isReject = IS_WEBVIEW || IS_MOBILE_BROWSER || isBizUser || isCorpUser || isOverquota || isFamilyUser;

                if (isReject) {
                    reject();
                    return;
                }

                resolve();
            },
        })
    );
};
